import type { ComponentProps } from 'react';

import { Progress, type ProgressProps } from '@vs-shop/ui/progress';
import { clsxm } from '@vs-shop/utils';

/**
 * How close an order is to earning its shipping:
 *
 *   <FreeShippingProgress>
 *     <FreeShippingProgressRemaining>
 *       <FreeShippingProgressLabel>Do dopravy zdarma vám ještě zbývá</FreeShippingProgressLabel>
 *       <FreeShippingProgressAmount>450 Kč</FreeShippingProgressAmount>
 *     </FreeShippingProgressRemaining>
 *     <FreeShippingProgressTrack value={77.5} />
 *   </FreeShippingProgress>
 *
 * Once the order clears the threshold the tenant composes the notice instead of
 * the remaining row, and runs the track at full — which arrangement applies is
 * the composing JSX's decision, never a prop, because the fact it turns on is a
 * fact about the ORDER rather than an appearance.
 *
 * Nothing here knows a threshold, a currency or a promotion code. The amount
 * still owed arrives formatted, and the track takes the percentage the tenant
 * already computed from its own promotion data.
 */

/** The copy over the track. */
const freeShippingProgressClasses = 'flex flex-col gap-y-2.5';

export type FreeShippingProgressProps = ComponentProps<'div'>;

/** What the shopper still owes for free shipping, over how far they have come. */
export const FreeShippingProgress = ({ className, ...props }: FreeShippingProgressProps) => (
  <div
    data-slot="free-shipping-progress"
    className={clsxm(freeShippingProgressClasses, className)}
    {...props}
  />
);

/** The sentence that says the shipping is already earned, centred over the track. */
const freeShippingProgressNoticeClasses = 'flex justify-center';

/**
 * The sentence itself. Below the line a tenant writes: the notice is one part
 * with a centring box around its copy, so an exported recipe for the copy would
 * be a recipe with no caller.
 */
const freeShippingProgressNoticeCopyClasses = 'text-foreground-muted font-medium tracking-tight';

export type FreeShippingProgressNoticeProps = ComponentProps<'div'>;

/**
 * The order has earned its shipping, two elements deep: the centring box and
 * the sentence inside it. `className` lands on the BOX, which is what takes the
 * width.
 */
export const FreeShippingProgressNotice = ({
  className,
  children,
  ...props
}: FreeShippingProgressNoticeProps) => (
  <div
    data-slot="free-shipping-progress-notice"
    className={clsxm(freeShippingProgressNoticeClasses, className)}
    {...props}
  >
    <span
      data-slot="free-shipping-progress-notice-copy"
      className={freeShippingProgressNoticeCopyClasses}
    >
      {children}
    </span>
  </div>
);

/** What is still owed, opposed to how much: the sentence at one edge, the amount at the other. */
const freeShippingProgressRemainingClasses = 'flex justify-between gap-2';

export type FreeShippingProgressRemainingProps = ComponentProps<'div'>;

/** The sentence beside the amount still owed. */
export const FreeShippingProgressRemaining = ({
  className,
  ...props
}: FreeShippingProgressRemainingProps) => (
  <div
    data-slot="free-shipping-progress-remaining"
    className={clsxm(freeShippingProgressRemainingClasses, className)}
    {...props}
  />
);

/** What the amount beside it is for. */
const freeShippingProgressLabelClasses = 'text-foreground-muted tracking-tight';

export type FreeShippingProgressLabelProps = ComponentProps<'span'>;

/** What is still missing, in words. */
export const FreeShippingProgressLabel = ({
  className,
  ...props
}: FreeShippingProgressLabelProps) => (
  <span
    data-slot="free-shipping-progress-label"
    className={clsxm(freeShippingProgressLabelClasses, className)}
    {...props}
  />
);

/** The amount still owed, already formatted by whoever knows the currency. */
const freeShippingProgressAmountClasses = 'tracking-tight';

export type FreeShippingProgressAmountProps = ComponentProps<'span'>;

/** How much is still missing. */
export const FreeShippingProgressAmount = ({
  className,
  ...props
}: FreeShippingProgressAmountProps) => (
  <span
    data-slot="free-shipping-progress-amount"
    className={clsxm(freeShippingProgressAmountClasses, className)}
    {...props}
  />
);

export type FreeShippingProgressTrackProps = ProgressProps;

/**
 * How far the order has come, as a proportion of the threshold.
 *
 * A part rather than a bare `@vs-shop/ui` primitive at the call site, because
 * every consumer of this section needs the same one and would otherwise each
 * decide again: the cart page, the cart drawer and the checkout summary all
 * render the same bar under the same copy. `value` is a percentage the tenant
 * computed from its own promotion data — this part does no arithmetic.
 */
export const FreeShippingProgressTrack = (props: FreeShippingProgressTrackProps) => (
  <Progress data-slot="free-shipping-progress-track" {...props} />
);
